import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import darkLightImage from '../../../assets/dark-light.svg';
import lightImage from '../../../assets/light.svg';
import darkImage from '../../../assets/dark.svg';
// Direct import of OpenDyslexic font file for webpack to handle it
import OpenDyslexicFont from '../../../assets/fonts/OpenDyslexic-Regular.otf';

// Get API base URL from environment variable or use relative URL as fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// This function dynamically loads the OpenDyslexic font
const loadDyslexicFont = () => {
  return new Promise((resolve) => {
    // In production, use a more direct approach to font loading
    if (process.env.NODE_ENV === 'production') {
      // Create a link element to load the font file
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = `${process.env.PUBLIC_URL || ''}/dyslexic-font.css`;
      fontLink.onload = () => {
        console.log('Production font CSS loaded successfully');
        resolve();
      };
      fontLink.onerror = () => {
        console.warn('Failed to load production font CSS, using fallback');
        // Insert inline font-face as fallback
        const style = document.createElement('style');
        style.textContent = `
          @font-face {
            font-family: 'OpenDyslexic';
            src: url('${process.env.PUBLIC_URL || ''}/fonts/OpenDyslexic-Regular.otf') format('opentype');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
        document.head.appendChild(style);
        resolve();
      };
      document.head.appendChild(fontLink);
    } else {
      // Development approach using webpack import
      const style = document.createElement('style');
      style.textContent = `
        @font-face {
          font-family: 'OpenDyslexic';
          src: url('${OpenDyslexicFont}') format('opentype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
      resolve();
    }
    
    // Create a test element to force font loading
    const testDiv = document.createElement('div');
    testDiv.style.fontFamily = 'OpenDyslexic, sans-serif';
    testDiv.style.visibility = 'hidden';
    testDiv.style.position = 'absolute';
    testDiv.style.left = '-9999px';
    testDiv.textContent = 'Font Loading Test';
    document.body.appendChild(testDiv);
  });
};

const AppearanceSection = () => {
  const { darkMode, setDarkMode, resetToSystem } = useTheme();
  const [selectedColorMode, setSelectedColorMode] = useState(null);
  const [selectedFont, setSelectedFont] = useState(null);
  const [systemFont, setSystemFont] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fontLoaded, setFontLoaded] = useState(false);
  
  // Create scrollbar styles
  const scrollbarStylesWebkit = `
    .appearance-section::-webkit-scrollbar {
      width: 8px;
    }
    .appearance-section::-webkit-scrollbar-track {
      background: transparent;
    }
    .appearance-section::-webkit-scrollbar-thumb {
      background-color: ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
      border-radius: 20px;
    }
    @media (max-width: 1200px), (max-height: 800px) {
      .appearance-section::-webkit-scrollbar {
        width: 6px;
      }
      .appearance-section::-webkit-scrollbar-thumb {
        background-color: ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.18)'};
      }
    }
    @media (max-width: 900px), (max-height: 700px) {
      .appearance-section::-webkit-scrollbar {
        width: 5px;
      }
      .appearance-section::-webkit-scrollbar-thumb {
        background-color: ${darkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.16)'};
      }
    }
  `;

  // Function to get system font
  const detectSystemFont = () => {
    const defaultFonts = window.getComputedStyle(document.body).getPropertyValue('font-family');
    return defaultFonts.split(',')[0].trim().replace(/['"]/g, '');
  };

  // Load fonts on component mount
  useEffect(() => {
    loadDyslexicFont().then(() => {
      setFontLoaded(true);
      console.log('OpenDyslexic font loaded');
    });
  }, []);

  // Load saved preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No authentication token found, using default preferences');
          setSelectedColorMode('system');
          resetToSystem();
          setSelectedFont('default');
          setIsLoading(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 10000 // 10 second timeout
        });
        
        // Set initial color mode based on preferences or system default
        if (response.data?.preferences?.colorMode) {
          setSelectedColorMode(response.data.preferences.colorMode);
        } else {
          setSelectedColorMode('system');
          // If no preference is set, reset theme to follow system
          resetToSystem();
        }

        // Set initial font based on preferences or default
        if (response.data?.preferences?.fontPreference) {
          setSelectedFont(response.data.preferences.fontPreference);
        } else {
          setSelectedFont('default');
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        
        // More detailed error logging
        if (error.code === 'ECONNABORTED') {
          console.error('Connection timeout when loading preferences');
        } else if (error.response) {
          console.error('Server error when loading preferences:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('No response received when loading preferences');
        } else {
          console.error('Request error when loading preferences:', error.message);
        }
        
        // If we fail to load preferences, default to system/default values
        setSelectedColorMode('system');
        resetToSystem();
        setSelectedFont('default');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [resetToSystem]);

  // Effect to detect and update system font
  useEffect(() => {
    setSystemFont(detectSystemFont());
  }, []);

  // Effect to apply color mode changes - only runs after preferences are loaded
  useEffect(() => {
    if (isLoading || selectedColorMode === null) return;

    switch (selectedColorMode) {
      case 'dark':
        setDarkMode(true);
        break;
      case 'light':
        setDarkMode(false);
        break;
      case 'system':
        resetToSystem();
        break;
    }
  }, [selectedColorMode, setDarkMode, resetToSystem, isLoading]);

  // Effect to apply font changes - only runs after preferences are loaded
  useEffect(() => {
    if (isLoading || selectedFont === null || !systemFont) return;

    const root = document.documentElement;
    let fontFamily;

    switch (selectedFont) {
      case 'system':
        fontFamily = systemFont;
        root.style.setProperty('--chat-font-family', fontFamily);
        root.classList.remove('using-dyslexic-font');
        break;
      case 'dyslexic':
        // Set the font and add a class for easier debugging
        root.style.setProperty('--chat-font-family', 'OpenDyslexic, system-ui, sans-serif');
        root.classList.add('using-dyslexic-font');
        
        // Create a small CSS animation to force font redraw
        const styleEl = document.createElement('style');
        styleEl.textContent = `
          @keyframes fontRedraw {
            0% { opacity: 0.99; }
            100% { opacity: 1; }
          }
          .using-dyslexic-font {
            animation: fontRedraw 0.01s;
          }
        `;
        document.head.appendChild(styleEl);
        
        setTimeout(() => {
          if (styleEl.parentNode) {
            document.head.removeChild(styleEl);
          }
        }, 100);
        break;
      default:
        fontFamily = 'midus-sans, system-ui, sans-serif';
        root.style.setProperty('--chat-font-family', fontFamily);
        root.classList.remove('using-dyslexic-font');
    }
  }, [selectedFont, systemFont, isLoading, fontLoaded]);

  // Save preferences to database
  const savePreferences = async (colorMode, font) => {
    if (isUpdating) return;

    try {
      setIsUpdating(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await axios.patch(
        `${API_BASE_URL}/users/me`,
        {
          preferences: {
            colorMode,
            fontPreference: font
          }
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );

    } catch (error) {
      console.error('Error saving preferences:', error);
      
      // More detailed error logging without toasts
      if (error.code === 'ECONNABORTED') {
        console.error('Connection timeout when saving preferences');
      } else if (error.response) {
        console.error('Server error when saving preferences:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received when saving preferences');
      } else {
        console.error('Request error when saving preferences:', error.message);
      }
      
      // No need to revert UI state for network errors
      if (error.response && error.response.status !== 404 && 
          error.response.status !== 401 && error.response.status !== 403) {
        // For other server errors, revert the UI to the previous state
        setSelectedColorMode(prevColorMode => prevColorMode);
        setSelectedFont(prevFont => prevFont);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleColorModeChange = async (mode) => {
    setSelectedColorMode(mode);
    if (mode === 'system') {
      resetToSystem();
    } else {
      setDarkMode(mode === 'dark');
    }
    await savePreferences(mode, selectedFont);
  };

  const handleFontChange = (font) => {
    setSelectedFont(font);
    savePreferences(selectedColorMode, font);
  };

  const getColorModeClasses = (mode) => {
    const isSelected = selectedColorMode === mode;
    return `border ${isSelected ? 'border-black dark:border-white' : 'border-gray-300 dark:border-[#181818]'} rounded-2xl  px-3 pt-3 pb-4 cursor-pointer 
      hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white 
      ${isSelected ? 'bg-gray-50 dark:bg-[#181818] ring-1 ring-black dark:ring-white' : ''}`;
  };

  const getFontClasses = (font) => {
    const isSelected = selectedFont === font;
    return `border ${isSelected ? 'border-black dark:border-white' : 'border-gray-300 dark:border-[#181818]'} rounded-2xl p-4 cursor-pointer 
      hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white 
      ${isSelected ? 'bg-gray-50 dark:bg-[#181818] ring-1 ring-black dark:ring-white' : ''}`;
  };

  // Function to get preview font style
  const getPreviewFontStyle = (font) => {
    switch (font) {
      case 'system':
        return { fontFamily: systemFont };
      case 'dyslexic':
        return { fontFamily: 'OpenDyslexic, sans-serif' };
      default:
        return { fontFamily: 'midus-sans, system-ui, sans-serif' };
    }
  };

  return (
    <div className="appearance-section mt-2 border border-gray-200 dark:border-[#444444] rounded-2xl max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-thin p-6 max-[1200px]:p-5 max-[900px]:p-4 [@media(max-height:800px)]:p-5 [@media(max-height:700px)]:p-4" style={{ scrollbarWidth: 'thin', scrollbarColor: darkMode ? 'rgba(255, 255, 255, 0.1) transparent' : 'rgba(0, 0, 0, 0.2) transparent' }}>
      <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white max-[1200px]:text-lg max-[1200px]:mb-5 max-[900px]:text-base max-[900px]:mb-4 [@media(max-height:800px)]:text-lg [@media(max-height:800px)]:mb-5 [@media(max-height:700px)]:text-base [@media(max-height:700px)]:mb-4">Appearance</h2>
      
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white max-[1200px]:text-base max-[1200px]:mb-3 max-[900px]:text-sm max-[900px]:mb-2.5 [@media(max-height:800px)]:text-base [@media(max-height:800px)]:mb-3 [@media(max-height:700px)]:text-sm [@media(max-height:700px)]:mb-2.5">Color mode</h3>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 max-[1200px]:gap-3 max-[900px]:gap-2.5 [@media(max-height:800px)]:gap-3 [@media(max-height:700px)]:gap-2.5">
            <div 
              className={`${getColorModeClasses('light')} dark:hover:border-gray-600`}
              onClick={() => handleColorModeChange('light')}
              role="button"
              tabIndex={0}
            >
              <div className="h-24 flex items-center justify-center rounded-lg mb-4 overflow-hidden max-[1200px]:h-20 max-[1200px]:mb-3 max-[900px]:h-16 max-[900px]:mb-2.5 [@media(max-height:800px)]:h-20 [@media(max-height:800px)]:mb-3 [@media(max-height:700px)]:h-16 [@media(max-height:700px)]:mb-2.5">
                <img src={lightImage} alt="Light mode" className="w-full h-auto object-contain rounded-lg" />
              </div>
              <p className="text-center text-gray-900 dark:text-white">Light</p>
            </div>
            <div 
              className={`${getColorModeClasses('system')} dark:hover:border-gray-600`}
              onClick={() => handleColorModeChange('system')}
              role="button"
              tabIndex={0}
            >
              <div className="h-24 flex items-center justify-center rounded-lg mb-4 overflow-hidden max-[1200px]:h-20 max-[1200px]:mb-3 max-[900px]:h-16 max-[900px]:mb-2.5 [@media(max-height:800px)]:h-20 [@media(max-height:800px)]:mb-3 [@media(max-height:700px)]:h-16 [@media(max-height:700px)]:mb-2.5">
                <img src={darkLightImage} alt="Match system" className="w-full h-auto object-contain rounded-lg" />
              </div>
              <p className="text-center font-medium text-gray-900 dark:text-white">Match system</p>
            </div>
            <div 
              className={`${getColorModeClasses('dark')} dark:hover:border-gray-600`}
              onClick={() => handleColorModeChange('dark')}
              role="button"
              tabIndex={0}
            >
              <div className="h-24 flex items-center justify-center rounded-lg mb-4 overflow-hidden max-[1200px]:h-20 max-[1200px]:mb-3 max-[900px]:h-16 max-[900px]:mb-2.5 [@media(max-height:800px)]:h-20 [@media(max-height:800px)]:mb-3 [@media(max-height:700px)]:h-16 [@media(max-height:700px)]:mb-2.5">
                <img src={darkImage} alt="Dark mode" className="w-full h-auto object-contain rounded-lg" />
              </div>
              <p className="text-center text-gray-900 dark:text-white">Dark</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white max-[1200px]:text-base max-[1200px]:mb-3 max-[900px]:text-sm max-[900px]:mb-2.5 [@media(max-height:800px)]:text-base [@media(max-height:800px)]:mb-3 [@media(max-height:700px)]:text-sm [@media(max-height:700px)]:mb-2.5">Chat font</h3>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 max-[1200px]:gap-3 max-[900px]:gap-2.5 [@media(max-height:800px)]:gap-3 [@media(max-height:700px)]:gap-2.5">
            <div 
              className={`${getFontClasses('default')} dark:hover:border-gray-600`}
              onClick={() => handleFontChange('default')}
              role="button"
              tabIndex={0}
            >
              <div className="aspect-[5/1] pb-2 flex items-center justify-center text-2xl text-gray-900 dark:text-white max-[1200px]:text-xl max-[1200px]:pb-1.5 max-[900px]:text-lg max-[900px]:pb-1 [@media(max-height:800px)]:text-xl [@media(max-height:800px)]:pb-1.5 [@media(max-height:700px)]:text-lg [@media(max-height:700px)]:pb-1" style={getPreviewFontStyle('default')}>
                Aa
              </div>
              <p className="text-center font-medium text-gray-900 dark:text-white">Default</p>
            </div>
            <div 
              className={`${getFontClasses('system')} dark:hover:border-gray-600`}
              onClick={() => handleFontChange('system')}
              role="button"
              tabIndex={0}
            >
              <div className="aspect-[5/1] pb-2 flex items-center justify-center text-2xl text-gray-900 dark:text-white max-[1200px]:text-xl max-[1200px]:pb-1.5 max-[900px]:text-lg max-[900px]:pb-1 [@media(max-height:800px)]:text-xl [@media(max-height:800px)]:pb-1.5 [@media(max-height:700px)]:text-lg [@media(max-height:700px)]:pb-1" style={getPreviewFontStyle('system')}>
                Aa
              </div>
              <p className="text-center text-gray-900 dark:text-white">Match system</p>
            </div>
            <div 
              className={`${getFontClasses('dyslexic')} dark:hover:border-gray-600`}
              onClick={() => handleFontChange('dyslexic')}
              role="button"
              tabIndex={0}
            >
              <div className="aspect-[5/1] pb-2 flex items-center justify-center text-2xl text-gray-900 dark:text-white max-[1200px]:text-xl max-[1200px]:pb-1.5 max-[900px]:text-lg max-[900px]:pb-1 [@media(max-height:800px)]:text-xl [@media(max-height:800px)]:pb-1.5 [@media(max-height:700px)]:text-lg [@media(max-height:700px)]:pb-1" style={getPreviewFontStyle('dyslexic')}>
                Aa
              </div>
              <p className="text-center text-gray-900 dark:text-white">Dyslexic friendly</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add style tag for webkit scrollbars */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStylesWebkit }} />
    </div>
  );
};

export default AppearanceSection;